import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, throwError } from 'rxjs';
import { ProblemDetails, ValidationError } from '../models/validation.dto';
import { ToastService } from '../shared/services/toast.service';
import { CONTRACT_OVER_ALLOCATION, formatContractAmount, readOverAllocation } from '../utils/contract-quantity';

/**
 * Error codes the caller handles itself (for example by asking for confirmation and resending): no toast is shown
 * for them, the error is only passed on.
 */
export const HANDLED_ERROR_CODES = new HttpContextToken<readonly string[]>(() => []);

interface ErrorToast {
  message: string;
  title: string;
}

const DEFAULT_TITLE = 'Fout';

// Titles and messages are Dutch texts used as translation keys: the backoffice shows them as they are,
// the portal translates them.

// Friendly messages per backend '@Code'. The ProblemDetails 'detail' field is
// never displayed: for business errors it is an English developer message and
// for unexpected errors it carries no user-relevant information.
const CODE_MESSAGES: Record<string, ErrorToast> = {
  '@BaseAuditEntity.Delete.EntityHasDependencies': {
    message: 'Deze entiteit wordt al in de applicatie gebruikt, kan niet verwijderen',
    title: 'Fout bij verwijderen',
  },
  '@AfroepRevertSplitFailed': {
    message: 'Kan niet terugdraaien. Er zijn al delen van deze afroep ingepland',
    title: 'Fout bij verwijderen',
  },
  '@DifferentArticle': {
    message: 'Dit artikel is verschillend van het artikel in het transport.',
    title: 'Fout bij het plannen van dit artikel',
  },
  '@NoPercentageDrogeStof': {
    message: 'De percentages droge stof zijn niet of slechts gedeeltelijk ingevuld op het contract',
    title: 'Afroep kan nog niet goedgekeurd worden',
  },
  // Planning conflicts (HTTP 409): the grid reverts the affected ride and reloads the planning.
  '@Planning.VersionConflict': {
    message: 'Deze rit werd intussen door een andere planner gewijzigd. De planning werd opnieuw geladen.',
    title: 'Rit intussen gewijzigd',
  },
  '@Planning.AlreadyPlanned': {
    message: 'Dit item werd intussen al door een andere planner ingepland.',
    title: 'Reeds ingepland',
  },
  '@Planning.InvalidVersion': {
    message: 'De planning is verouderd. Vernieuw de pagina en probeer opnieuw.',
    title: 'Planning verouderd',
  },
  // Portal account without a linked customer (HTTP 403); the portal normally shows its "not linked" page first.
  '@Portal.NoLinkedRelation': {
    message: 'Uw account is nog niet gekoppeld aan een klant. Neem contact op met Plantefeve.',
    title: 'Account nog niet gekoppeld',
  },
  '@SalesInvoice.Report.InvoiceNotFound': {
    message: 'Deze factuur werd niet gevonden.',
    title: 'Fout bij factuur',
  },
  '@SalesInvoice.Report.NoInvoiceLines': {
    message: 'Deze factuur heeft geen factuurlijnen en kan niet verzonden worden.',
    title: 'Fout bij factuur',
  },
  '@SalesInvoice.Report.VatTypeMissing': {
    message: 'Het BTW-type van de factuurlijnen ontbreekt. Controleer de factuurlijnen.',
    title: 'Fout bij factuur',
  },
  // {{remaining}} and {{unit}} come from the error's data (see messageParams).
  [CONTRACT_OVER_ALLOCATION]: {
    message: 'Er resten nog {{remaining}} {{unit}} op dit contract.',
    title: 'Onvoldoende resterend',
  },
};

// Exceptions to the rule above: the TAS integration writes its business errors in Dutch for the planner
// (e.g. why a TAS dossier cannot be linked), so their 'detail' is shown as-is, under a title per area.
function userFacingDetailTitle(code: string): string | undefined {
  if (code.startsWith('@Tas.')) {
    return 'TAS';
  }
  if (code.startsWith('@Planning.Rendu.')) {
    return 'Niet ingepland';
  }
  if (code === '@TransportLockedByTas') {
    return 'Op slot bij TAS';
  }
  if (code === '@Parameter.Update.InvalidColorCode') {
    return 'Ongeldige kleur';
  }
  // Backoffice-only rules on the remaining amount of a contract and its lines.
  if (code === '@Contract.Update.UnitLocked' || code.startsWith('@PurchaseContractLine.')) {
    return 'Niet opgeslagen';
  }
  return undefined;
}

function statusMessage(status: number): ErrorToast {
  switch (status) {
    case 0:
      return {
        message: 'De server is niet bereikbaar. Controleer uw internetverbinding en probeer opnieuw.',
        title: 'Netwerkfout',
      };
    case 400:
      return { message: 'De aanvraag is ongeldig. Controleer uw invoer en probeer opnieuw.', title: DEFAULT_TITLE };
    case 401:
      return { message: 'U bent niet (meer) aangemeld. Meld u opnieuw aan.', title: 'Niet aangemeld' };
    case 403:
      return { message: 'U heeft geen rechten om deze actie uit te voeren.', title: 'Geen toegang' };
    case 404:
      return { message: 'De gevraagde gegevens werden niet gevonden.', title: DEFAULT_TITLE };
    case 409:
      return {
        message: 'Deze actie is in conflict met de huidige gegevens. Vernieuw de pagina en probeer opnieuw.',
        title: DEFAULT_TITLE,
      };
    default:
      return { message: 'Er is een onverwachte fout opgetreden. Probeer het later opnieuw.', title: DEFAULT_TITLE };
  }
}

function resolveErrorToast(err: HttpErrorResponse, pd: ProblemDetails, translate: TranslateService): ErrorToast {
  const detailTitle = pd?.code && pd.detail ? userFacingDetailTitle(pd.code) : undefined;
  if (detailTitle) {
    return { message: pd.detail!, title: detailTitle };
  }

  const mapped = pd?.code ? CODE_MESSAGES[pd.code] : undefined;
  const { message, title } = mapped ?? statusMessage(err.status);
  const params = messageParams(err, translate);
  const toast = { message: interpolate(translate.instant(message, params), params), title: translate.instant(title) };

  // Support reference for unexpected (unmapped) errors.
  if (!mapped && pd?.id) {
    return { ...toast, message: `${toast.message} (${translate.instant('referentie')}: ${pd.id})` };
  }

  return toast;
}

function messageParams(err: HttpErrorResponse, translate: TranslateService): Record<string, string> {
  const overAllocation = readOverAllocation(err);
  if (!overAllocation) {
    return {};
  }

  const amount = formatContractAmount(overAllocation.remaining, overAllocation.unit);
  const number = amount.slice(0, amount.lastIndexOf(' '));
  const unit = overAllocation.unit === 'trucks' ? translate.instant('truckUnitOther') : overAllocation.unit;
  return { remaining: number, unit: unit === 'truckUnitOther' ? 'trucks' : unit };
}

// The backoffice has no translation files, so ngx-translate returns the key without filling in its parameters.
function interpolate(text: string, params: Record<string, string>): string {
  return text.replace(/{{\s*(\w+)\s*}}/g, (match, key: string) => params[key] ?? match);
}

export const httpErrorLoggingInterceptor: HttpInterceptorFn = (req, next) => {
  // Resolved only when a request fails: TranslateService loads its translations through this interceptor while it
  // is being constructed, and injecting it (or ToastService, which uses it) up front is a circular dependency.
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      const translate = injector.get(TranslateService);
      // err.error is only ProblemDetails for JSON responses; it can also be a
      // Blob (blob-typed requests) or a ProgressEvent (network errors).
      const pd = (
        err.error &&
        typeof err.error === 'object' &&
        !(err.error instanceof Blob) &&
        !(err.error instanceof ProgressEvent)
          ? err.error
          : {}
      ) as ProblemDetails;

      // Validation (ASP.NET Core returns status 400 with { errors: { field: [msg] } })
      if ((err.status === 400 || err.status === 422) && pd?.errors && Object.keys(pd.errors).length) {
        const vErr: ValidationError = {
          isValidationError: true,
          status: err.status,
          title: translate.instant('Validatiefout'),
          errors: pd.errors,
        };
        // No toast here; the form will show inline messages.
        return throwError(() => vErr);
      }

      if (pd?.code && req.context.get(HANDLED_ERROR_CODES).includes(pd.code)) {
        return throwError(() => err);
      }

      // Non-validation HTTP errors → toast with a friendly message.
      const { message, title } = resolveErrorToast(err, pd, translate);
      injector.get(ToastService).showError(message, title);

      return throwError(() => err);
    })
  );
};
